// !client-only

import { Locale, isValidPostcode } from '@fixter/i18n';
import { getSegmentFromLocale } from '$util/getSegment/getSegmentFromLocale';
import { vrmUKRegex, vrmFRRegex } from '$constants/regexp';

export const sanitizeString = (str: FormDataEntryValue | string) => String(str).replace(/\s+/g, '').trim();

export const postcodeRegex =
  // eslint-disable-next-line max-len
  /^([Gg][Ii][Rr] 0[Aa]{2})|((([A-Za-z][0-9]{1,2})|(([A-Za-z][A-Ha-hJ-Yj-y][0-9]{1,2})|(([A-Za-z][0-9][A-Za-z])|([A-Za-z][A-Ha-hJ-Yj-y][0-9][A-Za-z]?))))\s?[0-9][A-Za-z]{2})$/i;

export class VrmLookupError extends Error {
  type: string;

  constructor(message: string, type: string) {
    super(message);
    this.name = 'VrmLookupError';
    this.type = type || 'PostcodeError' || 'VrmError';
  }
}

export function createPostcodeSchema(
  locale: Locale,
  textPostcodeInvalid: string,
  textPostcodeRequired: string
) {
  const validatePostcode = (value: string) => {
    let postcode = value;
    if (locale === Locale.FR_FR) {
      if (!postcode || postcode.trim().length < 1) {
        throw new VrmLookupError(textPostcodeRequired, 'PostcodeError');
      }
      if (!isValidPostcode(sanitizeString(postcode), getSegmentFromLocale(Locale.FR_FR))) {
        throw new VrmLookupError(textPostcodeInvalid, 'PostcodeError');
      }
    } else {
      postcode = postcode.trim().toUpperCase();
      if (!postcode || postcode.length < 1 || postcode.length > 8 || !postcodeRegex.test(postcode)) {
        throw new VrmLookupError(textPostcodeInvalid, 'PostcodeError');
      }
      if (!isValidPostcode(sanitizeString(postcode), getSegmentFromLocale(Locale.EN_GB))) {
        throw new VrmLookupError(textPostcodeInvalid, 'PostcodeError');
      }
    }
    return postcode;
  };

  return { validate: validatePostcode };
}

export function isValidVrm(vrm: string, locale: Locale): boolean {
  let regex;
  switch (locale) {
    case Locale.FR_FR:
      regex = vrmFRRegex;
      break;
    case Locale.EN_GB:
    default:
      regex = vrmUKRegex;
      break;
  }
  return regex.test(vrm);
}
export function createVrmSchema(locale: Locale, textVrmInvalid: string, textVrmRequired: string) {
  const validateVrm = (value: string) => {
    let vrm = value;
    if (locale === Locale.FR_FR) {
      if (!vrm || vrm.trim().length < 1) {
        throw new VrmLookupError(textVrmRequired, 'VrmError');
      }
      if (!isValidVrm(sanitizeString(vrm), Locale.FR_FR)) {
        throw new VrmLookupError(textVrmInvalid, 'VrmError');
      }
    } else {
      vrm = vrm.trim().toUpperCase();
      if (!vrm || vrm.length < 1 || vrm.length > 8) {
        throw new VrmLookupError(textVrmInvalid, 'VrmError');
      }
      if (!isValidVrm(sanitizeString(vrm), Locale.EN_GB)) {
        throw new VrmLookupError(textVrmInvalid, 'VrmError');
      }
    }
    return vrm;
  };

  return { validate: validateVrm };
}
